import { useQuery, useMutation, useQueryClient  } from "@tanstack/react-query";
import { User, PlusCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChildModal from './ChildModal';
import Portal from '../../../../components/common/Portal';
import { usePassport } from '../../../../hooks/usePassport';

const ChildrenSection = () => {
    const API_URL = import.meta.env.VITE_API_URL;
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [children, setChildren] = useState([]);
    const [passport, setPassport] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedChild, setSelectedChild] = useState();

    const { data : memberData } = useQuery({
        queryKey: ['member'],
        queryFn: async () => {
            const response = await fetch(`${API_URL}/bbs/ajax.get_member.php`);
            return response.json();
        }
    });
    const member = memberData?.data ?? null;

    const { data : passportList } = useQuery({
        queryKey: ['passportList'],
        queryFn: async () => {
            const response = await fetch(`${API_URL}/bbs/ajax.get_passport_list.php`);
            return response.json();
        }
    });
    const passportData = passportList?.data ?? null;

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const params = new URLSearchParams();
            params.append("ch_id", id);

            const response = await fetch(`${API_URL}/bbs/ajax.delete_child.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: params,
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error("네트워크 오류");
            }

            return response.json();
        },
        onSuccess: (response) => {
            if(!response.success) {
                switch (response.code) {
                    case "INVALID_REQUEST":
                        alert("필수 필드값이 입력되지 않았습니다.");
                        break;
                    default :
                        alert("알 수 없는 오류가 발생했습니다.");
                        break;
                }
                return;
            }

            if(!response.data) {
                alert("알 수 없는 오류가 발생했습니다.");
                return;
            }

            if(response.data.memberId) {
                alert("삭제되었습니다.");
                queryClient.invalidateQueries({queryKey: ['member']});
            }
        },
        onError: (error: any) => {
            console.log(error.message);
        }
    });

    const handleChildDelete = (id) => {
        if(!confirm("자녀정보와 연동된 여권정보가 삭제될 수 있습니다.\n정말로 삭제하시겠습니까?")) {
            return;
        }

        if(children.length <= 1) {
            alert("최소 한 명의 자녀 정보는 입력해야 합니다.");
            return;
        }

        deleteMutation.mutate(id);
    };

    const handleChildModal = (id?: string) => {
        setSelectedChild(id);
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (member?.children) {
            setChildren(member.children);
        }
    }, [member]);

    useEffect(() => {
        if (passportList?.data) {
            setPassport(passportList.data);
        }
    }, [passportList]);

	return (
		<section className="space-y-3">
			<div className="flex flex-wrap gap-2 items-baseline">
				<h2 className="text-m text-gray-500 font-bold tracking-tighter">자녀</h2>
				<span className="text-xs text-slate-400 font-medium">자녀는 최대 4명까지 등록 가능합니다.</span>
			</div>
            <div className="flex flex-col gap-4">
                {children?.map((child) => {
                    const item = passport?.find((passportItem) => passportItem.childId === child.id);
                    return (
                        <div key={child.id} className="w-full flex md:flex-row flex-col gap-4">
                            <div 
                              onClick={() => handleChildModal(child.id)}
                              className="relative p-5 border rounded-xl bg-gray-50 flex flex-col cursor-pointer hover:border-purple-400 transition-colors group w-full md:w-[50%] min-h-[190px] items-end "
                            >
                                <div className="flex space-x-3 mb-1">
                                    <button 
                                        className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleChildModal(child.id);
                                        }}
                                    >
                                        수정
                                    </button>
                                    <button 
                                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleChildDelete(child.id);
                                        }}
                                    >
                                        삭제
                                    </button>
                                </div>
                                <div className="flex flex-col justify-center w-full text-left ">
                                    <div className="flex-shrink-0 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm mb-3">
                                        <User className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <div className="flex-1 min-w-0 text-left">
                                        <p className="text-[15px] font-bold text-gray-900 truncate">
                                            {child.name || "이름 없음"}
                                        </p>
                                    </div>
                                    <div className="mt-1 space-y-0.5">
                                        <p className="text-xs text-gray-500 flex items-center">
                                            <span className="w-16 text-gray-400 tracking-tighter flex-shrink-0">성별</span>
                                            <span>{child.gender === "M" ? "남자" : child.gender === "F" ? "여자" : "-"}</span>
                                        </p>
                                    </div>
                                    <div className="mt-1 space-y-0.5">
                                        <p className="text-xs text-gray-500 flex items-center">
                                            <span className="w-16 text-gray-400 tracking-tighter flex-shrink-0">생년월일</span>
                                            <span>{child.birth || "-"}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {item ? (
                                <div
                                    onClick={() => navigate('/passport')} 
                                    className="cursor-pointer group relative overflow-hidden rounded-[12px] border-2 border-slate-200 hover:border-blue-400 transition-all shadow-sm hover:shadow-md min-h-[190px] md:w-[48%] bg-white flex flex-col">
                                    <div className="bg-blue-900 h-6 flex items-center px-4 justify-between shrink-0">
                                        <span className="text-[8px] text-blue-200 tracking-[0.1em] font-bold uppercase">AI WORLD</span>
                                        <div className="flex gap-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 opacity-50" ></div>
                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 opacity-50" ></div>
                                        </div>
                                    </div>
                                    <div className="py-2 px-4 flex-1 flex flex-col justify-center">
                                        <div className="space-y-2">
                                            <div className="border-b border-gray-50 pb-1">
                                                <p className="text-[9px] text-gray-400 font-bold uppercase leading-none mb-1">Surname / Given Name</p>
                                                <p className="font-bold text-lg text-slate-800">{item.name}</p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <p className="text-[9px] text-gray-400 font-bold uppercase leading-none mb-1">Passport No.</p>
                                                    <p className="font-mono text-[13px] font-bold text-slate-700 tracking-tight">{item.code}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[9px] text-gray-400 font-bold uppercase leading-none mb-1">Grade</p>
                                                    <p className="font-bold text-[13px] text-slate-700">{item.grade}학년</p>
                                                </div>
                                                <div>
                                                    <p className="text-[9px] text-gray-400 font-bold uppercase leading-none mb-1">Level</p>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[13px] font-black text-blue-600">LV.</span>
                                                        <span className="font-mono text-[13px] font-bold text-slate-700">{item.level}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-end justify-end">
                                                    {item.existPassword ? (
                                                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-500 border border-blue-100 font-bold">인증</span>
                                                    ) : (
                                                        <div className="flex items-center justify-end gap-1 relative group">
                                                            <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-50 text-red-500 border border-red-100 font-bold">
                                                                미인증
                                                            </span>
                                                            <div className="relative">
                                                                <div className="w-4 h-4 rounded-full border border-gray-300 text-gray-400 text-[10px] flex items-center justify-center cursor-pointer bg-white">
                                                                    ?
                                                                </div>
                                                                <div className="absolute bottom-6 right-0 hidden group-hover:block z-10">
                                                                    <div className="bg-gray-900 text-white text-[10px] rounded-md px-2 py-1 whitespace-nowrap shadow-lg">
                                                                        비밀번호가 설정되지 않은 여권입니다.
                                                                    </div>
                                                                    <div className="absolute top-full right-2 w-2 h-2 bg-gray-900 rotate-45 -mt-1"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    onClick={() => navigate('/passport')} 
                                    className="relative overflow-hidden rounded-[12px] border-2 border-gray-200 bg-gray-100 opacity-60 min-h-[190px] md:w-[48%] flex flex-col cursor-pointer">
                                    <div className="bg-gray-400 h-6 flex items-center px-4 justify-between shrink-0">
                                        <span className="text-[8px] text-gray-200 tracking-[0.1em] font-bold uppercase">
                                            AI WORLD
                                        </span>

                                        <div className="flex gap-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-gray-300 opacity-50"></div>
                                            <div className="w-1.5 h-1.5 rounded-full bg-gray-300 opacity-50"></div>
                                        </div>
                                    </div>

                                    <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                                        <p className="text-sm font-bold text-gray-500 mb-1">
                                            등록된 여권 없음
                                        </p>

                                        <p className="text-xs text-gray-400">
                                            연결된 AI 여권이 없습니다.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {children.length < 4 && (
                <button
                    type="button"
                    onClick={() => handleChildModal(null)}
                    className="w-full border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-2 text-gray-400 hover:bg-gray-50 hover:border-gray-300 transition-colors group"
                >
                    <div className="p-3 rounded-full  transition-transform">
                        <PlusCircle className="w-8 h-8" />
                    </div>
                </button>
            )}
            <Portal>
                {isModalOpen && (
                    <ChildModal
                        id={selectedChild}
                        onClose={() => setIsModalOpen(false)} 
                    />
                )}
            </Portal>
        </section>
	);
}

export default ChildrenSection;